import React, { Fragment, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MdOutlineDescription, MdPeople, MdInsights } from 'react-icons/md';
import { IoClose } from 'react-icons/io5';
import { ClockIcon, UserIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import colors from '../../config/colors';
import ImpersonationModal from './ImpersonationModal';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { t } = useTranslation('common');
  const { isAdmin } = useAuth();
  const [isImpersonationModalOpen, setIsImpersonationModalOpen] = useState(false);
  
  const navigation = [
    { name: t('navigation.consultation'), href: '/consultation', icon: MdOutlineDescription },
    { name: t('navigation.history'), href: '/history', icon: ClockIcon },
    // { name: t('navigation.patients'), href: '/patients', icon: MdPeople }, // Hidden for now
    { name: t('navigation.statistics'), href: '/statistics', icon: MdInsights },
  ];

  return (
    <>
      {/* Mobile backdrop */}
      <Transition
        show={isOpen}
        as={Fragment}
        enter="transition-opacity ease-linear duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 lg:hidden z-20"
          onClick={onClose}
        />
      </Transition>

      {/* Sidebar */}
      <Transition
        show={isOpen}
        as={Fragment}
        enter="transition ease-in-out duration-300 transform"
        enterFrom="-translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in-out duration-300 transform"
        leaveFrom="translate-x-0"
        leaveTo="-translate-x-full"
      >
        <div 
          className={`fixed inset-y-0 left-0 w-[100px] flex flex-col lg:relative lg:translate-x-0 z-30`}
          style={{ backgroundColor: colors.primary.background }}
        >
          {/* Close button - mobile only */}
          <button
            onClick={onClose}
            className="lg:hidden absolute top-4 -right-10 p-2 rounded-md text-white hover:bg-white hover:bg-opacity-10"
          >
            <IoClose className="w-6 h-6" />
          </button>

          {/* Impersonation button - Admin only */}
          {isAdmin && (
            <div className="pt-8 pb-4 pl-2 pr-2">
              <button
                onClick={() => setIsImpersonationModalOpen(true)}
                className="flex flex-col items-center group"
                title={t('impersonation.button')}
              >
                <div 
                  className="p-3 rounded-xl transition-colors text-black"
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primary.hover)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                >
                  <UserIcon className="w-6 h-6" />
                </div>
                <span className="mt-1 text-xs font-medium text-black">
                  {t('impersonation.button')}
                </span>
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 flex flex-col items-center justify-center space-y-8">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className="flex flex-col items-center group"
                  title={item.name}
                  onClick={() => onClose()}
                >
                  <div 
                    className="p-3 rounded-xl transition-colors text-black"
                    style={
                      isActive 
                        ? { backgroundColor: colors.primary.selected }
                        : {}
                    }
                    onMouseEnter={(e) => !isActive && (e.currentTarget.style.backgroundColor = colors.primary.hover)}
                    onMouseLeave={(e) => !isActive && (e.currentTarget.style.backgroundColor = '')}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className="mt-1 text-xs font-medium text-black">
                    {item.name}
                  </span>
                </Link>
              );
            })}
          </nav>
        </div>
      </Transition>

      {/* Desktop sidebar - always visible */}
      <div 
        className={`hidden lg:flex w-[100px] flex-col items-center h-screen`}
        style={{ backgroundColor: colors.primary.background }}
      >
        {/* Impersonation button - Admin only */}
        {isAdmin && (
          <div className="pt-8 pb-4 pl-2 pr-2">
            <button
              onClick={() => setIsImpersonationModalOpen(true)}
              className="flex flex-col items-center group"
              title={t('impersonation.button')}
            >
              <div 
                className="p-3 rounded-xl transition-colors text-black"
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primary.hover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
              >
                <UserIcon className="w-6 h-6" />
              </div>
              <span className="mt-1 text-xs font-medium text-black">
                {t('impersonation.button')}
              </span>
            </button>
          </div>
        )}

        <nav className="flex-1 flex flex-col items-center justify-center space-y-8">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className="flex flex-col items-center group"
                title={item.name}
              >
                <div 
                  className="p-3 rounded-xl transition-colors text-black"
                  style={
                    isActive 
                      ? { backgroundColor: colors.primary.selected }
                      : {}
                  }
                  onMouseEnter={(e) => !isActive && (e.currentTarget.style.backgroundColor = colors.primary.hover)}
                  onMouseLeave={(e) => !isActive && (e.currentTarget.style.backgroundColor = '')}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <span className="mt-1 text-xs font-medium text-black">
                  {item.name}
                </span>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Impersonation Modal */}
      <ImpersonationModal
        isOpen={isImpersonationModalOpen}
        onClose={() => setIsImpersonationModalOpen(false)}
      />
    </>
  );
};

export default Sidebar; 